import { useState } from 'react';
import { Link } from 'react-router';
import { Button, Checkbox, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Pagination, Select, toast } from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';
import { useHasRole } from '@/providers/AuthProvider';
import {
  TAX_INVOICE_WRITE_ROLES,
  fetchAllPages,
  fetchIssues,
  useTaxInvoiceIssueMutations,
  useTaxInvoiceIssuesQuery,
  type IssueSearch,
  type IssueSummary,
  type PrintData,
} from '../api/taxInvoiceApi';
import { downloadCsv, formatBusinessNo, formatDateTime, todayKst, toCsv, won } from '../lib/taxInvoiceLib';
import { TaxInvoicePrintModal } from './TaxInvoicePrintModal';
import { TaxInvoiceSearchForm, defaultIssueSearch, useTaxInvoiceLabels } from './TaxInvoiceSearchForm';

/*
 * 발행 내역 리스트(고도몰 "세금계산서 발행 내역 리스트") — 종류별 합계표, 목록, 선택 삭제(발행 취소), 인쇄, 엑셀.
 * 발행 취소 후 남은 발행분이 없으면 신청은 발행 요청 리스트로 돌아간다(BE).
 */

const PAGE_SIZES = [10, 20, 50, 100] as const;
/** 합계표 행 — 고도몰 상단 표 순서. 없는 종류는 0 으로 채운다. */
const SUMMARY_ROWS: ReadonlyArray<{ invoiceKind: string; issueType: string }> = [
  { invoiceKind: 'TAX_INVOICE', issueType: 'GENERAL' },
  { invoiceKind: 'INVOICE', issueType: 'GENERAL' },
  { invoiceKind: 'TAX_INVOICE', issueType: 'ELECTRONIC' },
  { invoiceKind: 'INVOICE', issueType: 'ELECTRONIC' },
];

export const fillSummary = (summary: ReadonlyArray<IssueSummary>): IssueSummary[] =>
  SUMMARY_ROWS.map(
    (k) =>
      summary.find((s) => s.invoiceKind === k.invoiceKind && s.issueType === k.issueType) ?? {
        ...k,
        companyCount: 0,
        issueCount: 0,
        totalAmount: 0,
        supplyAmount: 0,
        vatAmount: 0,
      },
  );

export const TaxInvoiceIssuesTab = () => {
  const L = useTaxInvoiceLabels();
  const { t } = L;
  const canWrite = useHasRole(TAX_INVOICE_WRITE_ROLES);
  const [search, setSearch] = useState<IssueSearch>(defaultIssueSearch);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState<number>(10);
  const query = useTaxInvoiceIssuesQuery(search, page - 1, size);
  const { cancel, print } = useTaxInvoiceIssueMutations();
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [printData, setPrintData] = useState<PrintData | null>(null);
  const [exporting, setExporting] = useState(false);

  const rows = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const summary = fillSummary(query.data?.summary ?? []);
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.issueId));
  const resetSelection = () => setSelected(new Set());
  const toggle = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  // 취소된 발행분은 다시 취소할 수 없다.
  const cancellableIds = rows.filter((r) => selected.has(r.issueId) && r.status !== 'CANCELED').map((r) => r.issueId);

  const doCancel = () =>
    cancel.mutate(cancellableIds, {
      onSuccess: (count) => {
        toast.success(t('orders.taxInvoice.toast.canceled', { defaultValue: '{{count}}건의 발행을 취소했습니다.', count }));
        setConfirmCancel(false);
        resetSelection();
      },
      onError: (e) => toast.error(friendlyMessage(e, t('orders.taxInvoice.toast.cancelFail', '발행을 취소하지 못했습니다.'))),
    });

  const doPrint = () =>
    print.mutate([...selected], {
      onSuccess: (data) => setPrintData(data),
      onError: (e) => toast.error(friendlyMessage(e, t('orders.taxInvoice.toast.printFail', '인쇄 자료를 불러오지 못했습니다.'))),
    });

  const exportCsv = async () => {
    setExporting(true);
    try {
      const all = await fetchAllPages((p, sz) => fetchIssues(search, p, sz));
      const header = [
        t('orders.taxInvoice.col.no', '번호'), t('orders.taxInvoice.col.requestedAt', '발행요청일'), t('orders.taxInvoice.col.orderNo', '주문번호'),
        t('orders.taxInvoice.col.orderer', '주문자'), t('orders.taxInvoice.col.orderStatus', '주문상태'), t('orders.taxInvoice.col.company', '회사명'),
        t('orders.taxInvoice.col.businessNo', '사업자번호'), t('orders.taxInvoice.col.ceo', '대표자'), t('orders.taxInvoice.col.total', '발행액'),
        t('orders.taxInvoice.col.supply', '공급가액'), t('orders.taxInvoice.col.vat', '세액'), t('orders.taxInvoice.col.issueDate', '발행일'),
        t('orders.taxInvoice.col.kind', '종류'), t('orders.taxInvoice.col.status', '발행상태'), t('orders.taxInvoice.col.approvalNo', '승인번호'),
        t('orders.taxInvoice.col.printedAt', '인쇄'),
      ];
      const body = all.map((r, i) => [
        i + 1, formatDateTime(r.requestedAt), r.orderNo, r.ordererName, L.orderStatus(r.orderStatus), r.companyName, formatBusinessNo(r.businessNo),
        r.ceoName, r.totalAmount, r.supplyAmount, r.vatAmount, r.issueDate ?? '', `${L.issueType(r.issueType)} ${L.invoiceKind(r.invoiceKind)}`,
        L.issueStatus(r.status), r.approvalNo ?? '', r.printedAt ? formatDateTime(r.printedAt) : '',
      ]);
      downloadCsv(`세금계산서_발행내역_${todayKst()}.csv`, toCsv(header, body));
    } catch (e) {
      toast.error(friendlyMessage(e, t('orders.taxInvoice.toast.exportFail', '엑셀을 만들지 못했습니다.')));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4" data-testid="tax-invoice-issues">
      <TaxInvoiceSearchForm
        initial={search}
        issueMode
        makeDefault={defaultIssueSearch}
        onSearch={(q) => {
          setSearch(q);
          setPage(1);
          resetSelection();
        }}
      />

      {/* 종류별 합계(고도몰 상단 표) — 검색 조건 기준. */}
      <div className="overflow-x-auto rounded-lg" style={{ border: '1px solid var(--divider-strong)' }}>
        <table className="w-full text-[13px]" data-testid="tax-invoice-summary">
          <thead style={{ background: 'var(--surface-1)', color: 'var(--content-muted)' }}>
            <tr>
              <th className="px-3 py-2 text-left">{t('orders.taxInvoice.summary.kind', '구분')}</th>
              <th className="px-3 py-2 text-right">{t('orders.taxInvoice.summary.companies', '업체수')}</th>
              <th className="px-3 py-2 text-right">{t('orders.taxInvoice.summary.count', '발행건수')}</th>
              <th className="px-3 py-2 text-right">{t('orders.taxInvoice.col.total', '발행액')}</th>
              <th className="px-3 py-2 text-right">{t('orders.taxInvoice.col.supply', '공급가액')}</th>
              <th className="px-3 py-2 text-right">{t('orders.taxInvoice.col.vat', '세액')}</th>
            </tr>
          </thead>
          <tbody>
            {summary.map((s) => (
              <tr key={`${s.issueType}-${s.invoiceKind}`} className="border-t" style={{ borderColor: 'var(--divider-soft)' }} data-testid="tax-invoice-summary-row">
                <td className="px-3 py-2">{`${L.issueType(s.issueType)} ${L.invoiceKind(s.invoiceKind)}`}</td>
                <td className="px-3 py-2 text-right tabular-nums">{s.companyCount.toLocaleString('ko-KR')}</td>
                <td className="px-3 py-2 text-right tabular-nums">{s.issueCount.toLocaleString('ko-KR')}</td>
                <td className="px-3 py-2 text-right tabular-nums">{won(s.totalAmount)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{won(s.supplyAmount)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{won(s.vatAmount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm" style={{ color: 'var(--content-default)' }}>
          {t('orders.taxInvoice.total', { defaultValue: '검색 {{total}}건', total: total.toLocaleString('ko-KR') })}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <Select className="w-24" value={String(size)} onChange={(e) => { setSize(Number(e.target.value)); setPage(1); }} aria-label={t('orders.taxInvoice.pageSize', '표시 개수')}>
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>{t('orders.taxInvoice.pageSizeOption', { defaultValue: '{{n}}개', n })}</option>
            ))}
          </Select>
          <Button size="sm" variant="outline" disabled={selected.size === 0 || print.isPending} onClick={doPrint} data-testid="tax-invoice-print">
            {t('orders.taxInvoice.print.action', '선택 인쇄')}
          </Button>
          <Button size="sm" variant="outline" onClick={() => void exportCsv()} disabled={exporting || total === 0} data-testid="tax-invoice-issues-excel">
            {exporting ? t('orders.taxInvoice.exporting', '만드는 중…') : t('orders.taxInvoice.excel', '엑셀 다운로드')}
          </Button>
          {canWrite ? (
            <Button size="sm" variant="danger" disabled={cancellableIds.length === 0} onClick={() => setConfirmCancel(true)} data-testid="tax-invoice-cancel">
              {t('orders.taxInvoice.cancel.action', '선택 삭제(발행 취소)')}
            </Button>
          ) : null}
        </div>
      </div>

      {query.isError ? (
        <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>
          {friendlyMessage(query.error, t('orders.taxInvoice.loadError', '목록을 불러오지 못했습니다.'))}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg" style={{ border: '1px solid var(--divider-strong)' }}>
          <table className="w-full min-w-[1100px] text-[13px]" data-testid="tax-invoice-issues-table">
            <thead style={{ background: 'var(--surface-1)', color: 'var(--content-muted)' }}>
              <tr>
                <th className="w-10 px-2 py-2">
                  <Checkbox checked={allOnPage} onCheckedChange={(v) => setSelected(v === true ? new Set(rows.map((r) => r.issueId)) : new Set())} aria-label={t('orders.taxInvoice.selectAll', '전체 선택')} />
                </th>
                <th className="px-2 py-2 text-left">{t('orders.taxInvoice.col.no', '번호')}</th>
                <th className="px-2 py-2 text-left">{t('orders.taxInvoice.col.requestedAt', '발행요청일')}</th>
                <th className="px-2 py-2 text-left">{t('orders.taxInvoice.col.orderNoOrderer', '주문번호 / 주문자')}</th>
                <th className="px-2 py-2 text-left">{t('orders.taxInvoice.col.orderStatus', '주문상태')}</th>
                <th className="px-2 py-2 text-left">{t('orders.taxInvoice.col.businessInfo', '사업자정보')}</th>
                <th className="px-2 py-2 text-right">{t('orders.taxInvoice.col.amounts', '발행액 / 공급가액 / 세액')}</th>
                <th className="px-2 py-2 text-left">{t('orders.taxInvoice.col.issueDate', '발행일')}</th>
                <th className="px-2 py-2 text-left">{t('orders.taxInvoice.col.kind', '종류')}</th>
                <th className="px-2 py-2 text-left">{t('orders.taxInvoice.col.status', '발행상태')}</th>
                <th className="px-2 py-2 text-left">{t('orders.taxInvoice.col.approvalNo', '승인번호')}</th>
                <th className="px-2 py-2 text-left">{t('orders.taxInvoice.col.printedAt', '인쇄')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-2 py-10 text-center" style={{ color: 'var(--content-muted)' }}>
                    {query.isPending ? t('orders.taxInvoice.loading', '불러오는 중…') : t('orders.taxInvoice.empty', '검색된 내역이 없습니다.')}
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={r.issueId} className="border-t align-top" style={{ borderColor: 'var(--divider-soft)', ...(r.status === 'CANCELED' ? { color: 'var(--content-muted)' } : {}) }} data-testid="tax-invoice-issue-row">
                    <td className="px-2 py-2 text-center">
                      <Checkbox checked={selected.has(r.issueId)} onCheckedChange={(v) => toggle(r.issueId, v === true)} aria-label={r.orderNo} />
                    </td>
                    <td className="px-2 py-2 tabular-nums">{total - ((page - 1) * size + i)}</td>
                    <td className="whitespace-nowrap px-2 py-2 tabular-nums">{formatDateTime(r.requestedAt)}</td>
                    <td className="px-2 py-2">
                      <Link to={`/orders/${r.orderId}`} className="text-[color:var(--accent-default)] underline">{r.orderNo}</Link>
                      <div style={{ color: 'var(--content-muted)' }}>{r.ordererName}</div>
                    </td>
                    <td className="whitespace-nowrap px-2 py-2">{L.orderStatus(r.orderStatus)}</td>
                    <td className="px-2 py-2">
                      <div>{r.companyName} · {formatBusinessNo(r.businessNo)}</div>
                      <div style={{ color: 'var(--content-muted)' }}>{r.ceoName}</div>
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
                      <div style={{ fontWeight: 600 }}>{won(r.totalAmount)}</div>
                      <div style={{ color: 'var(--content-muted)' }}>{won(r.supplyAmount)} / {won(r.vatAmount)}</div>
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 tabular-nums">{r.issueDate ?? '-'}</td>
                    <td className="whitespace-nowrap px-2 py-2">{`${L.issueType(r.issueType)} ${L.invoiceKind(r.invoiceKind)}`}</td>
                    <td className="whitespace-nowrap px-2 py-2">{L.issueStatus(r.status)}</td>
                    <td className="px-2 py-2 tabular-nums">{r.approvalNo ?? '-'}</td>
                    <td className="whitespace-nowrap px-2 py-2 tabular-nums">{r.printedAt ? formatDateTime(r.printedAt) : '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      <Pagination total={total} currentPage={page} pageSize={size} onPageChange={(p) => { setPage(p); resetSelection(); }} showGoTo={false} />

      <Dialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('orders.taxInvoice.cancel.title', '발행 취소')}</DialogTitle>
            <DialogDescription>
              {t('orders.taxInvoice.cancel.description', {
                defaultValue: '선택한 {{count}}건의 발행을 취소할까요? 남은 발행분이 없는 신청은 발행 요청 리스트로 돌아갑니다. 홈택스 발행분은 홈택스에서 따로 취소해야 합니다.',
                count: cancellableIds.length,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button size="sm" variant="ghost" onClick={() => setConfirmCancel(false)}>{t('orders.taxInvoice.close', '닫기')}</Button>
            <Button size="sm" variant="danger" disabled={cancel.isPending} onClick={doCancel} data-testid="tax-invoice-cancel-confirm">
              {t('orders.taxInvoice.cancel.confirm', '발행 취소')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {printData ? <TaxInvoicePrintModal data={printData} onClose={() => setPrintData(null)} /> : null}
    </div>
  );
};
